/**
 * S09 — END CARD
 *
 * Full ink background. On entry (IntersectionObserver), types the line
 * "LET'S TALK." character by character, then a bone-colored strike line
 * draws through the middle, then the email fades in below. Signature
 * cinematic closer.
 */

import { useEffect, useRef, useState } from "react";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy } from "@/lib/voice";

export function S09_EndCard() {
  const copy = sectionCopy.s09;
  const sectionRef = useRef<HTMLElement | null>(null);
  const [typed, setTyped] = useState("");
  const [struck, setStruck] = useState(false);
  const [emailVisible, setEmailVisible] = useState(false);
  const triggeredRef = useRef(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio > 0.3 &&
            !triggeredRef.current
          ) {
            triggeredRef.current = true;
            // Typewriter
            const target = copy.hero;
            let i = 0;
            const id = setInterval(() => {
              i++;
              setTyped(target.slice(0, i));
              if (i >= target.length) {
                clearInterval(id);
                setTimeout(() => setStruck(true), 300);
                setTimeout(() => setEmailVisible(true), 1100);
              }
            }, 70);
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [copy.hero]);

  return (
    <section
      ref={sectionRef}
      id="s09"
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <MonumentType word={copy.monument} theme="ink" />

      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          padding: "var(--space-lg)",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--fog)",
            marginBottom: "var(--space-xl)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta}
        </div>

        <div style={{ position: "relative", display: "inline-block" }}>
          <h1
            className="type-hero-lg"
            style={{
              color: "var(--bone)",
              margin: 0,
              minHeight: "1em",
            }}
          >
            {typed}
            {typed.length < copy.hero.length && (
              <span
                style={{
                  display: "inline-block",
                  width: "0.6ch",
                  marginLeft: "0.1ch",
                  animation: "blink 800ms steps(2) infinite",
                }}
              >
                |
              </span>
            )}
          </h1>
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "50%",
              left: "-2%",
              width: struck ? "104%" : 0,
              height: "3px",
              background: "var(--bone)",
              transform: "translateY(-50%)",
              transition:
                "width 680ms cubic-bezier(0.87,0,0.13,1)",
            }}
          />
        </div>

        <div
          style={{
            marginTop: "var(--space-xl)",
            fontFamily: "var(--f-mono)",
            fontSize: "1.125rem",
            textTransform: "uppercase",
            letterSpacing: "0.3em",
            color: "var(--fog)",
            opacity: emailVisible ? 1 : 0,
            transform: emailVisible ? "translateY(0)" : "translateY(10px)",
            transition:
              "opacity 1s cubic-bezier(0.25,1,0.5,1), transform 1s cubic-bezier(0.25,1,0.5,1)",
          }}
        >
          {copy.contact}
        </div>
      </div>
    </section>
  );
}
